import * as THREE from 'three';
import { getDefaultTrunkHeight } from '@aerialytic/designer-states/core';

export const delay = (fn, ms) => new Promise((resolve) => setTimeout(() => resolve(fn()), ms));

export const createCanvas2D = async (width = 1024, height = 1024) => {
  const maxSeconds = 3;
  const canvas = document.createElement('canvas');
  canvas.height = height;
  canvas.width = width;
  const e = new Date().getTime() + maxSeconds * 1000;
  while (!canvas.getContext('2d') && new Date().getTime() <= e) {
    await delay(100);
  }
  return canvas;
};

export const hashString32 = (str, seed = 2166136261) => {
  let h = seed >>> 0;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

export const buildShadingCacheKey = ({
  designId = null,
  dsmSrc = null,
  weatherDataset = null,
  segments = [],
  obstacles = [],
  trees = []
} = {}) => {
  const payload = {
    designId: designId || null,
    dsmSrc: dsmSrc || null,
    weatherDataset: weatherDataset || null,
    segments: (segments || []).map((s) => ({
      pitch: Number(s?.pitch) || 0,
      azimuth: Number(s?.azimuth) || 0,
      geometry: s?.geometry || []
    })),
    obstacles: (obstacles || []).map((o) => ({
      height: Number(o?.height) || 0,
      radius: Number(o?.radius) || 0,
      orientation: o?.orientation || o?.profile || null,
      shape: o?.shape || null,
      geometry: o?.geometry || []
    })),
    trees: (trees || []).map((t) => ({
      dsm: t?.dsm || null,
      height: Number(t?.height) || 0,
      trunkHeight: Number(t?.trunkHeight) || 0,
      radius: Number(t?.radius) || 0,
      geometry: t?.geometry || []
    }))
  };
  try {
    const str = JSON.stringify(payload);
    const h1 = hashString32(str, 2166136261);
    const h2 = hashString32(str, 2166136261 ^ 0x9e3779b9);
    return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`.slice(0, 12);
  } catch {
    return null;
  }
};

export const getRaycastableObjects = (objects = [], recursive = false) => {
  const raycastable = [];
  const append = (object) => {
    if (!object) return;
    if (typeof object.raycast === 'function') raycastable.push(object);
    if (recursive && Array.isArray(object.children)) {
      object.children.forEach(append);
    }
  };
  objects.forEach(append);
  return raycastable;
};

export const intersectRaycastableObjects = (raycaster, objects = [], recursive = false) => {
  const intersects = [];
  const appendIntersections = (object) => {
    if (!object) return;
    if (typeof object.raycast === 'function') raycaster.intersectObject(object, false, intersects);
    if (recursive && Array.isArray(object.children)) {
      object.children.forEach(appendIntersections);
    }
  };
  objects.forEach(appendIntersections);
  return intersects.sort((a, b) => a.distance - b.distance);
};

export class CanvasPool {
  PoolKeys = {
    SegmentIndexMask: 'SegmentIndexMask',
    GetTiff: 'GetTiff',
    GetTiffOrg: 'GetTiffOrg',
    GetTiffWithHouse: 'GetTiffWithHouse',
    GetTiffWithoutHouse: 'GetTiffWithoutHouse',
    GetMaskCanvas: 'GetMaskCanvas',
    GetAnualPanelsShadings: 'GetAnualPanelsShadings'
  };

  canvasPool = {};

  contextPool = {};

  imageDataPool = {};

  nodejs = false;

  defaultWidth = 1024;

  defaultHeight = 1024;

  constructor(createPool = true, size = null) {
    if (size && Number.isFinite(size.width) && Number.isFinite(size.height)) {
      this.defaultWidth = size.width;
      this.defaultHeight = size.height;
    }
    if (createPool) this.CreatePool();
  }

  CreateCanvas(width, height) {
    let canvas;
    if (!this.nodejs) {
      canvas = document.createElement('canvas');
      canvas.height = height;
      canvas.width = width;
    } else {
      canvas = global.createCanvas(width, height);
    }
    return canvas;
  }

  Create(key, width = null, height = null) {
    key = key.toLowerCase();

    if (this.canvasPool[key]) {
      this.Destroy(key);
    }
    const w = Number.isFinite(width) ? width : this.defaultWidth;
    const h = Number.isFinite(height) ? height : this.defaultHeight;
    const canvas = this.CreateCanvas(w, h);
    this.canvasPool[key] = canvas;

    if (!this.contextPool[key]) {
      const context = canvas.getContext('2d');
      this.contextPool[key] = context;
    }
  }

  Destroy(key) {
    key = key.toLowerCase();
    const canvas = this.canvasPool[key];
    canvas.width = 1;
    canvas.height = 1;
    this.canvasPool[key] = null;
    this.contextPool[key] = null;

    delete this.canvasPool[key];
    delete this.contextPool[key];
  }

  CreatePool() {
    Object.keys(this.PoolKeys).forEach((key) => {
      this.Create(key, this.defaultWidth, this.defaultHeight);
    });
  }

  getCanvas(key, width = null, height = null) {
    key = key.toLowerCase();
    const hasSize = Number.isFinite(width) && Number.isFinite(height);
    if (!(key in this.canvasPool)) this.Create(key, width, height);
    else if (hasSize) {
      const canvas = this.canvasPool[key];
      if (canvas && (canvas.width !== width || canvas.height !== height)) {
        this.Destroy(key);
        this.Create(key, width, height);
      }
    }
    return this.canvasPool[key];
  }

  getContext(key, width = null, height = null) {
    key = key.toLowerCase();
    this.getCanvas(key, width, height);
    const context = this.contextPool[key];
    if (!context) {
      this.Destroy(key);
      this.Create(key, width, height);
      return this.contextPool[key];
    }
    return context;
  }

  saveImageData(key, imageData = null) {
    key = key.toLowerCase();
    if (imageData === null) {
      const canvas = this.getCanvas(key);
      const context = this.getContext(key);
      imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    }
    this.imageDataPool[key] = imageData; // new ImageData(imageData.data, imageData.width, imageData.height);
  }

  getImageData(key) {
    key = key.toLowerCase();
    return this.imageDataPool[key];
  }
}

let ticTime = 0;
export function tic() {
  ticTime = Math.floor(Date.now());
}

export function toc(job) {
  const tocTime = Math.floor(Date.now()) - ticTime;
  // console.log(`${job} - time : ${tocTime}`);
}

export const downloadObjectAsJson = (json, exportName) => {
  const jsonStr = typeof json === 'string' ? json : JSON.stringify(json, null, 2);
  let blobUrl = null;
  const downloadAnchorNode = document.createElement('a');
  if (typeof Blob !== 'undefined' && typeof URL !== 'undefined' && URL.createObjectURL) {
    const blob = new Blob([jsonStr], { type: 'application/json' });
    blobUrl = URL.createObjectURL(blob);
    downloadAnchorNode.setAttribute('href', blobUrl);
  } else {
    downloadAnchorNode.setAttribute('href', `data:text/json;charset=utf-8,${encodeURIComponent(jsonStr)}`);
  }
  downloadAnchorNode.setAttribute('download', `${exportName}.json`);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
  if (blobUrl) {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
};

export const randId = () =>
  'xxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const getTreeData = (trees, scale, center, footPerUnit) => {
  const [cx, cy] = center;
  const c = 1.0;
  const newTrees = trees.map((tree) => {
    const geometry = Array.isArray(tree.geometry)
      ? tree.geometry
      : [
          [0, 0],
          [0, 0]
        ];
    const start = geometry[0] ?? [0, 0];
    const finish = geometry[1] ?? start;
    const r2 = (start[0] - finish[0]) ** 2 + (start[1] - finish[1]) ** 2;
    const baseRadius = Math.sqrt(Math.max(r2, 0));
    const convertToSceneUnits = (value) => (footPerUnit ? (value / footPerUnit) * scale : 0);
    const radiusFeet = baseRadius * (footPerUnit || 0);
    const numericHeight = Number(tree.height);
    const heightFeet = Number.isFinite(numericHeight) ? numericHeight : 0;
    // Match the default shown in the trees table/form (getDefaultTrunkHeight)
    // when a tree (e.g. AI-detected) has no real trunkHeight/trunk_height yet.
    const fallbackTrunkFeet = getDefaultTrunkHeight(heightFeet);
    const rawTrunkHeight = tree.trunkHeight ?? tree.trunk_height;
    // Number(null) === 0, which would be mistaken for a real "no trunk"
    // value instead of "not provided" — exclude null/undefined explicitly.
    const numericTrunkHeight = rawTrunkHeight == null ? NaN : Number(rawTrunkHeight);
    let trunkHeightFeet = Number.isFinite(numericTrunkHeight) ? numericTrunkHeight : fallbackTrunkFeet;
    if (heightFeet > 0) trunkHeightFeet = Math.min(trunkHeightFeet, heightFeet);
    if (!Number.isFinite(trunkHeightFeet)) trunkHeightFeet = 0;
    const pos = [(start[0] - cx / c) * scale, (start[1] - cy / c) * scale, 0];
    const end = [(finish[0] - cx / c) * scale, (finish[1] - cy / c) * scale, 0];
    return {
      position: pos,
      height: convertToSceneUnits(heightFeet),
      radius: baseRadius * scale,
      trunkHeight: convertToSceneUnits(trunkHeightFeet),
      treeType: tree.model,
      index: tree.id,
      end
    };
  });
  return newTrees;
};

export const filterDictByKeys = (object, keys) => {
  if (object !== null) {
    const filtered = Object.keys(object)
      .filter((key) => keys.includes(key))
      .reduce((obj, key) => {
        obj[key] = object[key];
        return obj;
      }, {});
    return filtered;
  }
};

const year = new Date().getFullYear();

export const daysInMonth = Array(12)
  .fill()
  .map((_, i) => 32 - new Date(year, i, 32).getDate());

export const daysOfWeek = () => {
  const weekdays = Array(12).fill(0);
  const weekends = Array(12).fill(0);
  daysInMonth.forEach((nDays, mo) => {
    for (let d = 0; d < nDays; d += 1) {
      const day = new Date(year, mo, d).getDay();
      if (day % 6 === 0) weekends[mo] += 1;
      else weekdays[mo] += 1;
    }
  });
  return [weekdays, weekends];
};

export const accumulateRenderTargets = (function () {
  let scratch, accumulateMaterial, copyMaterial, scene, camera, quadMesh;

  function init(rtAccum) {
    scratch = rtAccum.clone();

    accumulateMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tAccum: { value: null },
        tNew: { value: null }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        varying vec2 vUv;
        uniform sampler2D tAccum;
        uniform sampler2D tNew;
        void main() {
          float a = texture2D(tAccum, vUv).r;
          float b = texture2D(tNew,   vUv).r;
          gl_FragColor = vec4(a + b, 0.0, 0.0, 1.0);
        }
      `
    });

    copyMaterial = new THREE.MeshBasicMaterial({ map: scratch.texture });

    const geo = new THREE.PlaneGeometry(2, 2);
    quadMesh = new THREE.Mesh(geo, accumulateMaterial);
    scene = new THREE.Scene();
    scene.add(quadMesh);
    camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  }

  return function (rtAccum, rtNew, renderer) {
    if (!scratch) init(rtAccum);

    accumulateMaterial.uniforms.tAccum.value = rtAccum.texture;
    accumulateMaterial.uniforms.tNew.value = rtNew.texture;
    quadMesh.material = accumulateMaterial;
    renderer.setRenderTarget(scratch);
    renderer.clear();
    renderer.render(scene, camera);

    quadMesh.material = copyMaterial;
    renderer.setRenderTarget(rtAccum);
    renderer.clear();
    renderer.render(scene, camera);

    renderer.setRenderTarget(null);
  };
})();

const uuidv4 = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const shiftLatLong = (coordinates, v, xzy = false) => {
  const [centerLat, centerLon] = coordinates;
  const [dxCm, dyCm, dzCm] = [v.x, xzy ? v.z : v.y, xzy ? v.y : v.z];
  const degPerMeterLat = 1 / 111111;
  const degPerMeterLon = 1 / (111111 * Math.cos((centerLat * Math.PI) / 180));
  const dxMeter = dxCm / 100;
  const dyMeter = dyCm / 100;
  const newLat = centerLat - dyMeter * degPerMeterLat;
  const newLon = centerLon + dxMeter * degPerMeterLon;
  const dzFeet = dzCm * 0.0328;
  return [newLon, newLat, dzFeet];
};

const circleToPolygon3D = (center, radius, segments = 64) => {
  const [cx, cy, cz] = center;
  const pts = [];
  for (let i = 0; i < segments; i += 1) {
    const theta = (i / segments) * 2 * Math.PI;
    pts.push(new THREE.Vector3(cx + Math.cos(theta) * radius, cy + Math.sin(theta) * radius, cz));
  }
  return pts;
};

const toVector3 = (p) => {
  if (!p) return null;
  if (p.isVector3) return p;
  if (typeof p.x === 'number' && typeof p.y === 'number') {
    return new THREE.Vector3(p.x, p.y, typeof p.z === 'number' ? p.z : 0);
  }
  return null;
};

export const buildExport3DJson = ({
  state,
  segments = [],
  obstacles = [],
  trees = [],
  panelData = [],
  annotations = [],
  lead = null,
  coordinates = null,
  resolution = 7.5,
  scale = 0.2,
  width = 1024,
  height = 1024,
  includePanels = true,
  includeTrees = true,
  includeAnnotations = true
}) => {
  const vertices3D = state?.vertices3D || [];
  const verticesType = state?.verticesType || [];
  const center = state?.center || [0, 0, 1];
  const coords =
    Array.isArray(coordinates) && coordinates.length >= 2 ? [Number(coordinates[0]), Number(coordinates[1])] : [0, 0];
  const res = Number(resolution) || 7.5;
  const sc = Number(scale) || 0.2;
  const w = Number(width) || 1024;
  const h = Number(height) || 1024;

  const getLatLong = (v, xyz = false) => {
    const vec = toVector3(v);
    if (!vec) return [coords[1], coords[0], 0];
    const pos = vec
      .clone()
      .multiplyScalar(res / sc)
      .add(new THREE.Vector3(center[0] - w / 2, center[1] - h / 2, 0));
    return shiftLatLong(coords, pos, xyz);
  };

  const geoEncode = (poly, properties, shapeType) => {
    const ring = (Array.isArray(poly) ? poly : []).map((p) => toVector3(p)).filter(Boolean);
    let encoded = [ring.map((v) => getLatLong(v, false))];
    if (shapeType !== 'Point') {
      if (encoded[0].length) encoded[0].push(encoded[0][0]);
    } else {
      encoded = encoded[0][0];
    }
    let type = shapeType;
    if (type === 'Line') type = 'LineString';
    return {
      type: 'Feature',
      properties,
      geometry: {
        coordinates: encoded,
        type
      }
    };
  };

  const roofPolys = [];
  const obstaclePolys = [];
  vertices3D.forEach((poly, vi) => {
    if (verticesType[vi] === 'roof') roofPolys.push(poly);
    else obstaclePolys.push(poly);
  });

  const segmentsFeatures = roofPolys.map((poly, index) =>
    geoEncode(
      poly,
      {
        id: uuidv4(),
        index,
        type: 'face',
        azimuth: segments[index]?.azimuth,
        pitch: segments[index]?.pitch,
        height: segments[index]?.eaveHeight ?? segments[index]?.height ?? null
      },
      'Polygon'
    )
  );

  const obstacleFeatures = obstaclePolys.map((poly, index) =>
    geoEncode(
      poly,
      {
        id: uuidv4(),
        type: 'protrusions',
        index,
        radius: obstacles[index]?.radius,
        height: obstacles[index]?.height,
        shape: obstacles[index]?.shape
      },
      obstacles[index]?.shape || 'Polygon'
    )
  );

  let panelFeatures = [];
  if (includePanels && Array.isArray(panelData)) {
    panelFeatures = panelData
      .map((segmentPanels, index) => {
        if (!segmentPanels?.points3D) return [];
        return segmentPanels.points3D.map((pnl, j) =>
          geoEncode(
            pnl,
            {
              id: uuidv4(),
              type: 'panel',
              index,
              azimuth: segmentPanels.azimuth,
              tilt: segmentPanels.tilt,
              orientation: segmentPanels.points?.[j]?.[4] === 'h' ? 'horizontal' : 'vertical'
            },
            'Polygon'
          )
        );
      })
      .flat();
  }

  let treeFeatures = [];
  if (includeTrees && Array.isArray(trees)) {
    treeFeatures = trees.map((tree, index) => {
      const position = tree?.position || [0, 0, 0];
      const radius = tree?.radius || 0;
      return geoEncode(
        circleToPolygon3D(position, radius, 64),
        {
          id: uuidv4(),
          index,
          type: 'tree',
          radius: tree?.radius,
          height: tree?.height
        },
        'Polygon'
      );
    });
  }

  let annotationFeatures = [];
  if (includeAnnotations && Array.isArray(annotations)) {
    annotationFeatures = annotations.map((annot, index) =>
      geoEncode(
        [new THREE.Vector3(annot?.position?.x || 0, annot?.position?.y || 0, annot?.position?.z || 0)],
        {
          id: uuidv4(),
          index,
          type: 'annotation',
          text: annot?.text,
          annotation_type: annot?.type
        },
        'Point'
      )
    );
  }

  const property = lead?.property || {};
  const leadId = property.lead || lead?.uid || lead?.id || null;

  return {
    id: leadId,
    site: {
      address: {
        addressOne: `${property.street || ''}`,
        addressTwo: null,
        city: property.city || null,
        province: property.state || null,
        postalCode: property.zipcode || null
      },
      coordinateSystemOrigin: {
        latitude: coords[0],
        longitude: coords[1]
      },
      resolution: res / 30.48
    },
    geoJSON: {
      type: 'FeatureCollection',
      features: [...segmentsFeatures, ...obstacleFeatures, ...panelFeatures, ...treeFeatures, ...annotationFeatures]
    },
    setbacks: segments.map((s) => s?.setbacks ?? null),
    edgesType: segments.map((s) => s?.lines ?? null)
  };
};

export default {
  CanvasPool,
  delay,
  tic,
  toc,
  getTreeData,
  filterDictByKeys,
  daysInMonth,
  daysOfWeek,
  buildExport3DJson
};
